import React from "react";
import { Helmet } from "react-helmet-async";

const AboutUs = () => {
  return (
    <>
      <div className="h-28"></div> {/* Dedicated spacer */}
      <div className="container mx-auto px-4 py-10">
        <Helmet>
          <title>About Us - ZiwaTech</title>
          <meta name="description" content="Learn about ZiwaTech, our mission, values, and the expert team behind our professional device repair services." />
        </Helmet>

        <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-primary">
          <i className="fa-solid fa-building mr-2"></i>
          About ZiwaTech
        </h1>
        
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Our Story</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="mb-4">
                  Founded in 2011, ZiwaTech began with a simple mission: to provide high-quality, affordable device repair 
                  services to the North York community. What started as a small repair shop has grown into a trusted 
                  technology service center serving customers throughout the Greater Toronto Area.
                </p>
                <p>
                  Our journey began when our founder, a passionate tech enthusiast with years of experience in device 
                  repair, noticed a gap in the market for honest, transparent, and reliable repair services. Too often, 
                  customers were overcharged for simple repairs or pressured into buying new devices when their existing 
                  ones could be fixed.
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
                  alt="ZiwaTech Store Front" 
                  className="w-full h-auto"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "https://placehold.co/600x400/28c77b/ffffff?text=ZiwaTech+Store";
                  }}
                />
              </div>
            </div>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
            <p className="mb-4">
              At ZiwaTech, our mission is to extend the life of your devices through expert repair services, 
              reducing electronic waste and saving you money. We believe that with the right care and maintenance, 
              your devices can serve you longer than you might expect.
            </p>
            <div className="bg-gray-100 p-6 rounded-lg border-l-4 border-primary my-6">
              <p className="italic text-lg">
                "We're committed to providing honest, transparent, and high-quality repair services that put our 
                customers' needs first. Every device has a story, and we're here to make sure that story continues."
              </p>
            </div>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-primary">Integrity</h3>
                <p>
                  We believe in honest diagnostics and transparent pricing. We'll never recommend unnecessary 
                  repairs or replacements.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-primary">Expertise</h3>
                <p>
                  Our technicians are certified and continuously trained on the latest repair techniques and 
                  technologies.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-primary">Customer Focus</h3>
                <p>
                  Your satisfaction is our priority. We work to provide convenient, fast, and reliable service 
                  every time.
                </p>
              </div>
            </div>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Our Team</h2>
            <p className="mb-6">
              Our team consists of certified technicians with years of experience in repairing various devices. 
              From smartphones to computers, tablets to game consoles, our experts have the knowledge and skills 
              to diagnose and fix a wide range of issues.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-40 h-40 rounded-full overflow-hidden mx-auto mb-4 bg-gray-200">
                  <img 
                    src="/assets/team-1.jpg" 
                    alt="Team Member" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://placehold.co/300x300/28c77b/ffffff?text=Team";
                    }}
                  />
                </div>
                <h3 className="text-xl font-semibold">John Doe</h3>
                <p className="text-gray-600">Lead Technician</p>
              </div>
              <div className="text-center">
                <div className="w-40 h-40 rounded-full overflow-hidden mx-auto mb-4 bg-gray-200">
                  <img 
                    src="/assets/team-2.jpg" 
                    alt="Team Member" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://placehold.co/300x300/28c77b/ffffff?text=Team";
                    }}
                  />
                </div>
                <h3 className="text-xl font-semibold">Jane Smith</h3>
                <p className="text-gray-600">Phone Repair Specialist</p>
              </div>
              <div className="text-center">
                <div className="w-40 h-40 rounded-full overflow-hidden mx-auto mb-4 bg-gray-200">
                  <img 
                    src="/assets/team-3.jpg" 
                    alt="Team Member" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://placehold.co/300x300/28c77b/ffffff?text=Team";
                    }}
                  />
                </div>
                <h3 className="text-xl font-semibold">Michael Johnson</h3>
                <p className="text-gray-600">Computer Repair Expert</p>
              </div>
            </div>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Why Choose Us</h2>
            <ul className="list-disc pl-6 space-y-3">
              <li><strong>Fast Service:</strong> Most repairs are completed within 24-48 hours.</li>
              <li><strong>Quality Parts:</strong> We use only high-quality, manufacturer-grade replacement parts.</li>
              <li><strong>Warranty:</strong> All our repairs come with a 90-day warranty.</li>
              <li><strong>Competitive Pricing:</strong> Our rates are transparent and competitive.</li>
              <li><strong>Convenience:</strong> Located in York Gate Mall with ample parking and public transit access.</li>
              <li><strong>Data Protection:</strong> Your privacy is important to us. We follow strict protocols to protect your data.</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold mb-4">Visit Us Today</h2>
            <p className="mb-4">
              We invite you to visit our store at York Gate Mall in North York. Our friendly team is ready to help 
              you with all your device repair needs. No appointment necessary for consultations, though booking ahead 
              for repairs is recommended for faster service.
            </p>
            <div className="bg-primary text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Contact Information</h3>
              <p><strong>Address:</strong> A140-1 York Gate Blvd, North York, Toronto, ON M3N 3A1, Canada</p>
              <p><strong>Phone:</strong> +1647-200-2040</p>
              <p><strong>Email:</strong> repair@ziwatech.ca</p>
              <p><strong>Hours:</strong> Mon-Fri: 10am-8pm, Sat: 10am-6pm, Sun: 12pm-5pm</p>
            </div>
          </section>
        </div>
      </div>
      </div>
    </>
  );
};

export default AboutUs;
